import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from '../../Hooks/useIsomorphicLayoutEffect'
import useTabs from './hooks'
import { TabsProps } from './props'
import { Indicator, StyledTabs, Tab } from './styles'

const Tabs = ({
    activeTab,
    onChange = () => undefined,
    tabs,
    variant = 'default',
    ...props
}: TabsProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null)

    const indexTab = useMemo(
        () => tabs.findIndex((tab) => tab.value === activeTab),
        [activeTab, tabs]
    )
    const [currentTab, setCurrentTab] = useState(indexTab > -1 ? indexTab : 0)
    const [lineStyles, setLineStyles] = useState({ left: 0, width: 0 })
    const [isOverflown, setIsOverflown] = useState(false)

    const {
        draggers: { moveDrag, startDrag, stopDrag },
        isMoving,
        pressed,
    } = useTabs()

    useEffect(() => {
        if (indexTab > -1) setCurrentTab(indexTab)
    }, [indexTab])

    useIsomorphicLayoutEffect(() => {
        const wrapperDiv = wrapperRef.current
        if (wrapperDiv) {
            const current =
                wrapperDiv.children[currentTab].getBoundingClientRect()
            const first = wrapperDiv.children[0].getBoundingClientRect()

            setLineStyles({
                left: current.x - first.x,
                width: current.width,
            })
            setIsOverflown(wrapperDiv.scrollWidth > wrapperDiv.clientWidth)
        }
    }, [currentTab])

    const changeTab = useCallback(
        (index: number, newValue: string) => {
            setCurrentTab(index)
            onChange(newValue)
        },
        [onChange]
    )

    const tabsItems = useMemo(
        () =>
            tabs.map((tab, key) => (
                <Tab
                    {...tab}
                    active={key === currentTab}
                    css={{ pointerEvents: isMoving ? 'none' : 'all' }}
                    key={key}
                    onClick={() => changeTab(key, tab.value)}
                    variant={variant}
                >
                    {tab.label}
                </Tab>
            )),
        [tabs, currentTab, variant, isMoving, changeTab]
    )

    return (
        <StyledTabs
            {...props}
            css={{
                ...props.css,
                cursor: isOverflown
                    ? isMoving || pressed
                        ? 'grabbing'
                        : 'grab'
                    : 'pointer',
            }}
            {...(isOverflown && {
                onMouseDown: startDrag,
                onMouseMove: moveDrag,
                onMouseUp: stopDrag,
                onTouchEnd: stopDrag,
                onTouchMove: moveDrag,
                onTouchStart: startDrag,
            })}
            ref={wrapperRef}
            variant={variant}
        >
            {tabsItems}
            <Indicator css={lineStyles} />
        </StyledTabs>
    )
}

export default Tabs

export { StyledTabs }
